import React, { createRef, useContext, useEffect, useState } from "react";
import { CreateOfferContext } from "./context";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import NumberCircleIcon from "../../Common/NumberCircleIcon";

const OfferNav = () => {
  const {
    sectionRefs,
    rightContentRef,
    values,
    activeMainTab,
    setActiveMainTab,
    activeTab,
    setActiveTab,
    chargesActiveTab,
    setChargesActiveTab,
    usageActiveTab,
    setUsageActiveTab,
    touched,
    setTouched,
    errors,
    isEdit,
  } = useContext(CreateOfferContext);
  const [selectedItem, setSelectedItem] = useState("offerDetails");
  const [openSections, setOpenSections] = useState({
    grants: false,
    charges: false,
    usage: false,
  });

  const toggleSection = (section) => {
    section &&
      setOpenSections((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
  };

  const markAllTouched = (errors) => {
    if (Array.isArray(errors)) {
      return errors.map((item) => markAllTouched(item));
    }

    if (typeof errors === "object" && errors !== null) {
      return Object.keys(errors).reduce((acc, key) => {
        acc[key] = markAllTouched(errors[key]);
        return acc;
      }, {});
    }

    return true;
  };

  const handleNext = async (tab, isToggle = true) => {
    const obj = {
      offerName: true,
      offerCode: true,
      offerPriority: true,
      subscriptionStartsOn: true,
      timePeriodStart: true,
      timePeriodOptionStart: {
        value: true,
        label: true,
      },
      subscriptionEndsOn: true,
      timePeriod: true,
      timePeriodOption: {
        value: true,
        label: true,
      },
      description: true,
      offerStartsOn: true,
      offerEndsOn: true,
      usageId: true,
      eventType: true,
      resourceName: true,
      unitOfMeasurement: true,
      priority: true,
      perUnitRate: true,
      flatRate: true,
      serviceName: true,
      policyName: true,
      aggregateVolume: [],
      usageRatePlan: [],
      recurringUsageRatePlan: [],
      grants: [],
      recurringGrants: [],
      charges: [],
      recurringCharges: [],
      effectiveAt: true,
      pricingModel: [],
      categories: true,
      ...errors,
    };

    await setTouched(markAllTouched(obj), true);

    if (
      Object.keys(touched).length > 0 &&
      Object.keys(errors).length === 0 &&
      activeMainTab !== tab
    ) {
      setActiveMainTab(tab);
      isToggle && toggleSection(
        tab === 2 ? "grants" : tab === 3 ? "charges" : tab === 4 ? "usage" : ""
      );
    }
  };

  const handleScrollTo = (name) => {
    const nameRef = sectionRefs.current[name];
    if (nameRef?.current && rightContentRef?.current) {
      const container = rightContentRef.current;
      const offsetTop = nameRef.current.offsetTop - container.offsetTop;

      container.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (values?.grants?.length > 0) {
      values?.grants?.forEach((_, index) => {
        const refKey = `grants${index + 1}`;
        if (!sectionRefs.current[refKey]) {
          sectionRefs.current[refKey] =
            sectionRefs.current[refKey] || createRef();
        }
      });
    }
    if (values?.recurringGrants?.length > 0) {
      values?.recurringGrants?.forEach((_, index) => {
        const refKey = `recurringGrants${index + 1}`;
        if (!sectionRefs.current[refKey]) {
          sectionRefs.current[refKey] =
            sectionRefs.current[refKey] || createRef();
        }
      });
    }
    if (values?.charges?.length > 0) {
      values?.charges?.forEach((_, index) => {
        const refKey = `charges${index + 1}`;
        if (!sectionRefs.current[refKey]) {
          sectionRefs.current[refKey] =
            sectionRefs.current[refKey] || createRef();
        }
      });
    }
    if (values?.recurringCharges?.length > 0) {
      values?.recurringCharges?.forEach((_, index) => {
        const refKey = `recurringCharges${index + 1}`;
        if (!sectionRefs.current[refKey]) {
          sectionRefs.current[refKey] =
            sectionRefs.current[refKey] || createRef();
        }
      });
    }
    if (values?.recurringUsageRatePlan?.length > 0) {
      values?.recurringUsageRatePlan?.forEach((_, index) => {
        const refKey = `recurringUsageRatePlan${index + 1}`;
        if (!sectionRefs.current[refKey]) {
          sectionRefs.current[refKey] =
            sectionRefs.current[refKey] || createRef();
        }
      });
    }
  }, [
    values?.grants,
    values?.recurringGrants,
    values?.charges,
    values?.recurringCharges,
    values?.recurringUsageRatePlan,
  ]);

  return (
    <div className="stepper-container stepper-border ms-0 ps-2">
      {/* Step 1: Offer Details */}
      <div className="stepper-step" onClick={() => handleNext(1)}>
        <div
          className={`stepper-dot ${activeMainTab < 1 ? "inactive-dot" : ""}`}
        />
        <div
          className={`fw-semibold cursor-pointer ${
            activeMainTab === 1 ? "rv-text-primary-color" : ""
          }`}
          onClick={() => {
            setSelectedItem("offerDetails");
          }}
        >
          Offer Details
        </div>
        <hr className="me-2" />
      </div>

      {/* Step 2: Grants */}
      <div className="stepper-step" onClick={() => handleNext(2)}>
        <div
          className={`stepper-dot ${activeMainTab < 2 ? "inactive-dot" : ""}`}
        />
        <div
          className="fw-semibold d-flex justify-content-between align-items-center cursor-pointer pe-4"
          onClick={() => {
            activeMainTab === 2 && toggleSection("grants");
          }}
        >
          <span
            className={`${activeMainTab === 2 ? "rv-text-primary-color" : ""}`}
          >
            Grants
          </span>
          {openSections.grants ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </div>
        {openSections.grants && (
          <div className="mt-1 ps-0">
            {values?.grants?.map((grant, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e?.stopPropagation();
                  handleNext(2, false);
                  setActiveTab("1");
                  setSelectedItem(`grants${index + 1}`);
                  handleScrollTo(`grants${index + 1}`);
                }}
                className="cursor-pointer pt-2"
              >
                <span
                  className={`me-1 ${
                    selectedItem === `grants${index + 1}`
                      ? "rv-text-primary-color"
                      : ""
                  }`}
                >
                  - Grant {index > 0 ? index + 1 : ""}
                </span>{" "}
                <NumberCircleIcon />
              </div>
            ))}
            {values?.recurringGrants?.map((grant, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e?.stopPropagation();
                  handleNext(2, false);
                  setActiveTab("2");
                  setSelectedItem(`recurringGrants${index + 1}`);
                  handleScrollTo(`recurringGrants${index + 1}`);
                }}
                className="cursor-pointer pt-2"
              >
                <span
                  className={`me-1 ${
                    selectedItem === `recurringGrants${index + 1}`
                      ? "rv-text-primary-color"
                      : ""
                  }`}
                >
                  - Grant {index > 0 ? index + 1 : ""}
                </span>{" "}
                <AutorenewIcon
                  fontSize="inherit"
                  fontWeight="bold"
                  style={{ transform: "rotate(45deg)" }}
                />
              </div>
            ))}
          </div>
        )}
        <hr className="me-2" />
      </div>

      {/* Step 3: Charges */}
      <div className="stepper-step" onClick={() => handleNext(3)}>
        <div
          className={`stepper-dot ${activeMainTab < 3 ? "inactive-dot" : ""}`}
        />
        <div
          className="fw-semibold d-flex justify-content-between align-items-center cursor-pointer pe-4"
          onClick={() => {
            activeMainTab === 3 && toggleSection("charges");
          }}
        >
          <span
            className={`${activeMainTab === 3 ? "rv-text-primary-color" : ""}`}
          >
            Charges
          </span>
          {openSections.charges ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </div>
        {openSections.charges && (
          <div className="ps-0 mt-1">
            {values?.charges?.map((charge, index) => (
              <div
                key={index}
                className="cursor-pointer pt-2"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleNext(3, false);
                  setChargesActiveTab("1");
                  setSelectedItem(`charges${index + 1}`);
                  handleScrollTo(`charges${index + 1}`);
                }}
              >
                <span
                  className={`me-1 ${
                    selectedItem === `charges${index + 1}`
                      ? "rv-text-primary-color"
                      : ""
                  }`}
                >
                  - Charge {index > 0 ? index + 1 : ""}
                </span>
                <NumberCircleIcon />
              </div>
            ))}
            {values?.recurringCharges?.map((charge, index) => (
              <div
                key={index}
                className="cursor-pointer pt-2"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleNext(3, false);
                  setChargesActiveTab("2");
                  setSelectedItem(`recurringCharges${index + 1}`);
                  handleScrollTo(`recurringCharges${index + 1}`);
                }}
              >
                <span
                  className={`me-1 ${
                    selectedItem === `recurringCharges${index + 1}`
                      ? "rv-text-primary-color"
                      : ""
                  }`}
                >
                  {" "}
                  - Charge {index > 0 ? index + 1 : ""}
                </span>{" "}
                {charge?.type === "1" ? (
                  <NumberCircleIcon />
                ) : (
                  <AutorenewIcon
                    fontSize="inherit"
                    fontWeight="bold"
                    style={{ transform: "rotate(45deg)" }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        <hr className="me-2" />
      </div>

      {/* Step 4: Usage */}
      <div className="stepper-step" onClick={() => handleNext(4)}>
        <div
          className={`stepper-dot ${activeMainTab < 4 ? "inactive-dot" : ""}`}
        />
        <div
          className="fw-semibold d-flex justify-content-between align-items-center cursor-pointer pe-4"
          onClick={() => {
            activeMainTab === 4 && toggleSection("usage");
          }}
        >
          <span
            className={`${activeMainTab === 4 ? "rv-text-primary-color" : ""}`}
          >
            Usage
          </span>
          {openSections.usage ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </div>
        {openSections.usage && (
          <div className="ps-0 mt-1">
            {values?.recurringUsageRatePlan?.map((usage, index) => (
              <div
                key={index}
                className="cursor-pointer pt-2"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleNext(4, false);
                  setUsageActiveTab("2");
                  setSelectedItem(`recurringUsageRatePlan${index + 1}`);
                  handleScrollTo(`recurringUsageRatePlan${index + 1}`);
                }}
              >
                <span
                  className={`me-1 ${
                    selectedItem === `recurringUsageRatePlan${index + 1}`
                      ? "rv-text-primary-color"
                      : ""
                  }`}
                >
                  {" "}
                  - Usage {index > 0 ? index + 1 : ""}
                </span>
                {usage?.type === "1" ? (
                  <NumberCircleIcon />
                ) : (
                  <AutorenewIcon
                    fontSize="inherit"
                    fontWeight="bold"
                    style={{ transform: "rotate(45deg)" }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        <hr className="me-2" />
      </div>
    </div>
  );
};

export default OfferNav;
